import React from "react";
import styled from "@emotion/styled";
import { theme } from "@/theme";
import { Badge } from "@/store";

type ProductBadgeProps = Badge & {
  size?: "default" | "lg";
};

const TextBadge = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  right: 0;
  width: auto;
  max-width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background-color: hsl(var(--color-primary));
  z-index: 2;

  p {
    text-align: center;
    color: hsl(var(--color-white));
    margin: 0;
    padding: ${theme.spacing(1)} ${theme.spacing(2)};
    font-size: 14px;
    font-weight: 600;
    line-height: 130%;
    white-space: nowrap;
  }

  &.lg {
    max-width: 140px;
    p {
      font-size: 16px;
    }
  }

  ${theme.breakpoints.down("md")} {
    max-width: 100px;
    p {
      font-size: 12px;
      padding: ${theme.spacing(0.75)} ${theme.spacing(1.5)};
    }
    &.lg {
      max-width: 120px;
      p {
        font-size: 14px;
      }
    }
  }
`;

const ImageBadgeContainer = styled.div<{
  position: Badge["position"];
  width: number;
  size: "default" | "lg";
}>`
  pointer-events: none;
  position: absolute;
  ${(props) =>
    props.position === "top-right"
      ? `top: ${props.size === "lg" ? "12px" : "8px"};`
      : `bottom: ${props.size === "lg" ? "12px" : "8px"};`}
  right: ${(props) => (props.size === "lg" ? "12px" : "8px")};
  width: calc(
    ${(props) => props.width}% -
      ${(props) => (props.size === "lg" ? "24px" : "16px")}
  );
  display: flex;
  align-items: center;
  justify-content: flex-end;
  z-index: 2;

  img {
    width: 100%;
    height: auto;
    display: block;
    object-fit: contain;
    object-position: ${(props) =>
      props.position === "top-right" ? "top" : "bottom"};
    max-width: 100%;
  }
`;

export const ProductBadge = ({
  image,
  position = "top-right",
  width = 30,
  text,
  size = "default",
}: ProductBadgeProps) => {
  // If we have text, use the text-only badge (check for non-empty, non-whitespace text)
  if (text && text.trim()) {
    return (
      <TextBadge className={size}>
        <p>{text}</p>
      </TextBadge>
    );
  }

  // If we have an image but no text, use the image badge with positioning (check for non-empty image)
  if (image && image.trim()) {
    return (
      <ImageBadgeContainer position={position} width={width} size={size}>
        <img src={image} alt="" loading="lazy" />
      </ImageBadgeContainer>
    );
  }

  return null;
};
